import { Sun, Moon, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal } from "lucide-react";
import type { Form, FormType } from "@/types/forms";

interface ManifestSlotProps {
  type: "sod" | "eod";
  form: Form | null;
  loading?: boolean;
  onCreateClick: (type: FormType) => void;
  onEditClick: (form: Form) => void;
  onPublishClick: (form: Form) => void;
  onUnpublishClick: (form: Form) => void;
  onArchiveClick: (form: Form) => void;
}

const config = {
  sod: { label: "Start of Day", icon: Sun, formType: "manifest_sod" as FormType },
  eod: { label: "End of Day", icon: Moon, formType: "manifest_eod" as FormType },
};

export function ManifestSlot({
  type,
  form,
  loading,
  onCreateClick,
  onEditClick,
  onPublishClick,
  onUnpublishClick,
  onArchiveClick,
}: ManifestSlotProps) {
  const { label, icon: Icon, formType } = config[type];

  const isDraft = form?.status === "draft";
  const isPublished = form?.status === "published";

  return (
    <div className="flex items-center justify-between rounded-lg border p-4">
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 text-muted-foreground" />
        <div>
          <p className="font-medium">{label}</p>
          {form ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Badge variant={isPublished ? "default" : "secondary"}>
                {form.status}
              </Badge>
              <span>{form.title}</span>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No manifest configured</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {form ? (
          <>
            {isDraft && (
              <Button variant="outline" size="sm" onClick={() => onPublishClick(form)}>
                Publish
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => onEditClick(form)}>
              Edit
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {isPublished && (
                  <DropdownMenuItem onClick={() => onUnpublishClick(form)}>
                    Unpublish
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => onArchiveClick(form)}
                >
                  Archive
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        ) : (
          <Button
            size="sm"
            onClick={() => onCreateClick(formType)}
            disabled={loading}
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create
          </Button>
        )}
      </div>
    </div>
  );
}
